import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, UserCheck, Plus, Loader2, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BoxScoreStats - Live and Final box score tables for both teams
 * Includes athlete roster claiming ("This is me") and self-addition ("+ Add Athlete")
 */
export function BoxScoreStats({ 
  gameId, 
  gameState,
  defaultExpanded = true 
}) {
  const { token, isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('number');
  const [sortAsc, setSortAsc] = useState(true);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [myAthlete, setMyAthlete] = useState(null);
  const [claimDialog, setClaimDialog] = useState({ open: false, player: null, teamSide: null });
  const [addDialog, setAddDialog] = useState({ open: false, teamSide: null });
  const [addForm, setAddForm] = useState({ jersey_number: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (gameState?.player_stats) {
      setStats(gameState);
      setLoading(false);
    } else {
      fetchStats();
    }
  }, [gameId, gameState]);

  const fetchMyAthlete = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/athletes/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setMyAthlete(await res.json());
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  useEffect(() => {
    fetchMyAthlete();
  }, [fetchMyAthlete]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/state`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check if athlete already has a connection for this game
  const hasConnectionForGame = useCallback((teamSide) => {
    if (!myAthlete?.roster_connections) return false;
    return myAthlete.roster_connections.some(
      (c) => c.game_id === gameId
    );
  }, [myAthlete, gameId]);

  const handleClaim = async () => {
    if (!myAthlete || !claimDialog.player) return;
    setSaving(true);
    const teamSide = claimDialog.teamSide;
    const teamName = teamSide === 'home'
      ? (stats?.home_stats?.team_name || stats?.home_team?.team_name || 'Home')
      : (stats?.away_stats?.team_name || stats?.away_team?.team_name || 'Away');
    const teamId = teamSide === 'home'
      ? (stats?.home_stats?.team_id || `${gameId}_home`)
      : (stats?.away_stats?.team_id || `${gameId}_away`);

    try {
      const res = await fetch(`${API_URL}/api/athletes/${myAthlete.id}/roster-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          team_id: teamId,
          team_name: teamName,
          jersey_number: claimDialog.player.player_number || 0,
          game_id: gameId,
          game_name: `${stats?.home_stats?.team_name || stats?.home_team?.team_name || 'Home'} vs ${stats?.away_stats?.team_name || stats?.away_team?.team_name || 'Away'}`,
          notes: `Claimed as ${claimDialog.player.player_name || 'player'} #${claimDialog.player.player_number || '?'}`,
        }),
      });
      if (res.ok) {
        toast.success('Profile linked! Your roster connection has been created.');
        setClaimDialog({ open: false, player: null, teamSide: null });
        fetchMyAthlete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to claim roster spot');
      }
    } catch {
      toast.error('Network error');
    }
    setSaving(false);
  };

  const handleAddAthlete = async (e) => {
    e.preventDefault();
    if (!myAthlete || !addForm.jersey_number) return;
    setSaving(true);
    const teamSide = addDialog.teamSide;
    const teamName = teamSide === 'home'
      ? (stats?.home_stats?.team_name || stats?.home_team?.team_name || 'Home')
      : (stats?.away_stats?.team_name || stats?.away_team?.team_name || 'Away');
    const teamId = teamSide === 'home'
      ? (stats?.home_stats?.team_id || `${gameId}_home`)
      : (stats?.away_stats?.team_id || `${gameId}_away`);

    try {
      const res = await fetch(`${API_URL}/api/athletes/${myAthlete.id}/roster-connections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          team_id: teamId,
          team_name: teamName,
          jersey_number: parseInt(addForm.jersey_number),
          game_id: gameId,
          game_name: `${stats?.home_stats?.team_name || stats?.home_team?.team_name || 'Home'} vs ${stats?.away_stats?.team_name || stats?.away_team?.team_name || 'Away'}`,
          notes: addForm.notes || `Self-added to ${teamName} roster`,
        }),
      });
      if (res.ok) {
        toast.success('You\'ve been added to the roster!');
        setAddDialog({ open: false, teamSide: null });
        setAddForm({ jersey_number: '', notes: '' });
        fetchMyAthlete();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add to roster');
      }
    } catch {
      toast.error('Network error');
    }
    setSaving(false);
  };

  const sortPlayers = (players) => {
    if (!players) return [];
    return [...players].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'number') {
        aVal = a.player_number || 999;
        bVal = b.player_number || 999;
      }
      if (typeof aVal === 'string') {
        return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortAsc ? aVal - bVal : bVal - aVal;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(field === 'number');
    }
  };

  const SortHeader = ({ field, label, className = '' }) => (
    <th 
      className={`py-2 px-2 text-xs font-medium text-zinc-400 cursor-pointer hover:text-white transition-colors ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field && (
          sortAsc ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  const PlayerRow = ({ player, teamSide }) => {
    const canClaim = isAuthenticated() && myAthlete && !hasConnectionForGame(teamSide);

    return (
      <tr className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors group">
        <td className="py-2 px-2 text-sm text-zinc-400 font-mono">
          {player.player_number || '-'}
        </td>
        <td className="py-2 px-2 text-sm text-white font-medium">
          {player.player_name || 'N/A'}
        </td>
        <td className="py-2 px-2 text-center">
          {canClaim ? (
            <button
              onClick={() => setClaimDialog({ open: true, player, teamSide })}
              className="text-xs text-rooam-blue hover:text-white bg-rooam-blue/10 hover:bg-rooam-blue/20 px-2 py-0.5 rounded transition-all opacity-60 group-hover:opacity-100"
              data-testid={`claim-btn-${player.player_id || player.player_number}`}
            >
              This is me
            </button>
          ) : null}
        </td>
        <td className="py-2 px-2 text-sm text-white font-bold tabular-nums text-center">
          {player.PTS}
        </td>
        <td className="py-2 px-2 text-sm text-zinc-300 tabular-nums text-center">
          {player.AST}
        </td>
        <td className="py-2 px-2 text-sm text-zinc-300 tabular-nums text-center">
          {player.PF}
        </td>
        <td className="py-2 px-2 text-sm text-zinc-300 tabular-nums text-center">
          {player.STL}
        </td>
        <td className="py-2 px-2 text-sm text-zinc-300 tabular-nums text-center">
          {player.BLK}
        </td>
        <td className="py-2 px-2 text-sm text-zinc-300 tabular-nums text-center">
          {player.TO}
        </td>
        <td className="py-2 px-2 text-xs text-zinc-400 tabular-nums text-center">
          {player.FGM}/{player.FGA}
        </td>
        <td className="py-2 px-2 text-xs text-zinc-400 tabular-nums text-center">
          {player.TPM}/{player.TPA}
        </td>
        <td className="py-2 px-2 text-xs text-zinc-400 tabular-nums text-center">
          {player.FTM}/{player.FTA}
        </td>
      </tr>
    );
  };

  const TeamTotalsRow = ({ players }) => {
    const totals = players.reduce((acc, p) => ({
      PTS: acc.PTS + (p.PTS || 0),
      REB: acc.REB + (p.REB || 0),
      AST: acc.AST + (p.AST || 0),
      STL: acc.STL + (p.STL || 0),
      BLK: acc.BLK + (p.BLK || 0),
      TO: acc.TO + (p.TO || 0),
      PF: acc.PF + (p.PF || 0),
      FGM: acc.FGM + (p.FGM || 0),
      FGA: acc.FGA + (p.FGA || 0),
      TPM: acc.TPM + (p.TPM || 0),
      TPA: acc.TPA + (p.TPA || 0),
      FTM: acc.FTM + (p.FTM || 0),
      FTA: acc.FTA + (p.FTA || 0),
    }), { PTS: 0, REB: 0, AST: 0, STL: 0, BLK: 0, TO: 0, PF: 0, FGM: 0, FGA: 0, TPM: 0, TPA: 0, FTM: 0, FTA: 0 });

    return (
      <tr className="bg-zinc-800/50 font-semibold">
        <td className="py-2 px-2 text-sm text-white" colSpan={3}>TEAM TOTALS</td>
        <td className="py-2 px-2 text-sm text-white font-bold tabular-nums text-center">{totals.PTS}</td>
        <td className="py-2 px-2 text-sm text-white tabular-nums text-center">{totals.AST}</td>
        <td className="py-2 px-2 text-sm text-white tabular-nums text-center">{totals.PF}</td>
        <td className="py-2 px-2 text-sm text-white tabular-nums text-center">{totals.STL}</td>
        <td className="py-2 px-2 text-sm text-white tabular-nums text-center">{totals.BLK}</td>
        <td className="py-2 px-2 text-sm text-white tabular-nums text-center">{totals.TO}</td>
        <td className="py-2 px-2 text-xs text-zinc-300 tabular-nums text-center">{totals.FGM}/{totals.FGA}</td>
        <td className="py-2 px-2 text-xs text-zinc-300 tabular-nums text-center">{totals.TPM}/{totals.TPA}</td>
        <td className="py-2 px-2 text-xs text-zinc-300 tabular-nums text-center">{totals.FTM}/{totals.FTA}</td>
      </tr>
    );
  };

  const TeamStatsTable = ({ teamName, teamColor, players, teamSide }) => {
    const sortedPlayers = sortPlayers(players);
    const canAddSelf = isAuthenticated() && myAthlete && !hasConnectionForGame(teamSide);
    
    return (
      <div className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800">
        {/* Team Header */}
        <div 
          className="px-4 py-3 flex items-center gap-3"
          style={{ background: `linear-gradient(90deg, ${teamColor}20, transparent)` }}
        >
          <div className="w-3 h-8 rounded" style={{ backgroundColor: teamColor }} />
          <h3 className="text-white font-bold text-lg">{teamName}</h3>
          <span className="text-zinc-400 text-sm ml-auto">
            {players.length} players
          </span>
        </div>
        
        {/* Stats Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-zinc-800/50 border-b border-zinc-700">
                <SortHeader field="number" label="#" className="text-left w-12" />
                <SortHeader field="player_name" label="ATHLETE" className="text-left" />
                <th className="py-2 px-2 text-xs font-medium text-zinc-400 text-center w-16">CLAIM</th>
                <SortHeader field="PTS" label="PTS" className="text-center w-12" />
                <SortHeader field="AST" label="AST" className="text-center w-12" />
                <SortHeader field="PF" label="F" className="text-center w-12" />
                <SortHeader field="STL" label="STL" className="text-center w-12" />
                <SortHeader field="BLK" label="BLK" className="text-center w-12" />
                <SortHeader field="TO" label="TO" className="text-center w-12" />
                <th className="py-2 px-2 text-xs font-medium text-zinc-400 text-center">FG</th>
                <th className="py-2 px-2 text-xs font-medium text-zinc-400 text-center">3PT</th>
                <th className="py-2 px-2 text-xs font-medium text-zinc-400 text-center">FT</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.length > 0 ? (
                <>
                  {sortedPlayers.map((player, idx) => (
                    <PlayerRow key={player.player_id || idx} player={player} teamSide={teamSide} />
                  ))}
                  <TeamTotalsRow players={players} />
                </>
              ) : (
                <tr>
                  <td colSpan={12} className="py-8 text-center text-zinc-500">
                    No player stats available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Add Athlete CTA */}
        {canAddSelf && (
          <div className="border-t border-zinc-800 px-4 py-3 text-center" data-testid={`add-athlete-section-${teamSide}`}>
            <p className="text-zinc-400 text-sm mb-2">Can't find your player in the list?</p>
            <Button
              size="sm"
              className="bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
              onClick={() => setAddDialog({ open: true, teamSide })}
              data-testid={`add-athlete-btn-${teamSide}`}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Athlete
            </Button>
          </div>
        )}
        {isAuthenticated() && !myAthlete && (
          <div className="border-t border-zinc-800 px-4 py-3 text-center">
            <p className="text-zinc-400 text-sm mb-2">Want to link your profile to this game?</p>
            <Button
              size="sm"
              variant="outline"
              className="border-rooam-blue text-rooam-blue hover:bg-rooam-blue hover:text-black"
              asChild
            >
              <Link to="/athlete/create">Create Athlete Profile</Link>
            </Button>
          </div>
        )}
        {!isAuthenticated() && (
          <div className="border-t border-zinc-800 px-4 py-3 text-center">
            <p className="text-sm text-zinc-500">
              <Link to="/login" className="text-rooam-blue hover:underline">Sign in</Link>{' '}
              to claim your roster spot
            </p>
          </div>
        )}
        {isAuthenticated() && myAthlete && hasConnectionForGame(teamSide) && (
          <div className="border-t border-zinc-800 px-4 py-3 text-center">
            <Badge className="bg-green-500/10 text-green-400 border-green-500 text-xs">
              <Link2 className="h-3 w-3 mr-1" />
              Profile linked to this game
            </Badge>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-pulse text-zinc-400">Loading stats...</div>
      </div>
    );
  }

  const getPlayersArray = () => {
    if (!stats?.player_stats) return [];
    if (Array.isArray(stats.player_stats)) return stats.player_stats;
    return Object.values(stats.player_stats);
  };

  const playersArray = getPlayersArray();
  const homePlayers = playersArray.filter(p => p.team_side === 'home') || [];
  const awayPlayers = playersArray.filter(p => p.team_side === 'away') || [];
  const homeTeam = stats?.home_team || stats?.home_stats || {};
  const awayTeam = stats?.away_team || stats?.away_stats || {};

  return (
    <>
      <div className="bg-zinc-950" data-testid="box-score-stats">
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3 bg-zinc-900 border-b border-zinc-800 cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <h2 className="text-white font-bold text-lg flex items-center gap-2">
            Stats
            {stats?.status === 'final' && (
              <span className="text-xs bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded">FINAL</span>
            )}
          </h2>
          <Button variant="ghost" size="sm" className="text-zinc-400">
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </Button>
        </div>
        
        {expanded && (
          <div className="p-4 space-y-6">
            <TeamStatsTable 
              teamName={homeTeam?.team_name || 'Home Team'}
              teamColor={homeTeam?.team_color || '#0066CC'}
              players={homePlayers}
              teamSide="home"
            />
            <TeamStatsTable 
              teamName={awayTeam?.team_name || 'Away Team'}
              teamColor={awayTeam?.team_color || '#CC0000'}
              players={awayPlayers}
              teamSide="away"
            />
          </div>
        )}
      </div>

      {/* Claim Dialog */}
      <Dialog open={claimDialog.open} onOpenChange={(open) => !open && setClaimDialog({ open: false, player: null, teamSide: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-700" data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Claim Roster Spot</DialogTitle>
          </DialogHeader>
          {claimDialog.player && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Confirm this is you on the game roster:
              </p>
              <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-rooam-blue/20 flex items-center justify-center">
                    <span className="text-rooam-blue font-bold">
                      #{claimDialog.player.player_number ?? '?'}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-semibold">{claimDialog.player.player_name || 'Unknown'}</p>
                    <p className="text-zinc-400 text-sm">
                      {claimDialog.teamSide === 'home'
                        ? (stats?.home_stats?.team_name || stats?.home_team?.team_name)
                        : (stats?.away_stats?.team_name || stats?.away_team?.team_name)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-rooam-blue/5 border border-rooam-blue/20 rounded-lg p-3">
                <p className="text-xs text-zinc-400 mb-1">Linking to your profile:</p>
                <p className="text-white font-medium">{myAthlete?.name}</p>
                <p className="text-xs text-zinc-500">{myAthlete?.sport} {myAthlete?.position && `· ${myAthlete.position}`}</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-zinc-600 text-zinc-300"
                  onClick={() => setClaimDialog({ open: false, player: null, teamSide: null })}
                  data-testid="claim-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold"
                  onClick={handleClaim}
                  disabled={saving}
                  data-testid="claim-confirm-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                    <><UserCheck className="h-4 w-4 mr-2" />This is Me</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Athlete Dialog */}
      <Dialog open={addDialog.open} onOpenChange={(open) => !open && setAddDialog({ open: false, teamSide: null })}>
        <DialogContent className="bg-zinc-900 border-zinc-700" data-testid="add-athlete-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Add Yourself to Roster</DialogTitle>
            <p className="text-xs text-zinc-400">
              Add your profile to the{' '}
              <span className="text-white font-medium">
                {addDialog.teamSide === 'home'
                  ? (stats?.home_stats?.team_name || stats?.home_team?.team_name || 'Home')
                  : (stats?.away_stats?.team_name || stats?.away_team?.team_name || 'Away')}
              </span>{' '}
              roster for this game
            </p>
          </DialogHeader>
          <form onSubmit={handleAddAthlete} className="space-y-4">
            <div>
              <Label className="text-zinc-300">Your Jersey Number</Label>
              <Input
                type="number"
                value={addForm.jersey_number}
                onChange={(e) => setAddForm({ ...addForm, jersey_number: e.target.value })}
                placeholder="Jersey # you wore"
                className="bg-zinc-800 border-zinc-700 text-white"
                required
                data-testid="add-jersey-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Notes (optional)</Label>
              <Input
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder="e.g. Guest player, sub"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <Button
              type="submit"
              disabled={saving || !addForm.jersey_number}
              className="w-full bg-rooam-blue hover:bg-rooam-blue-light text-black font-semibold disabled:opacity-40"
              data-testid="add-athlete-submit-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <><Plus className="h-4 w-4 mr-2" />Add to Roster</>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default BoxScoreStats;
